import {
  LiveChanelPlayer,
  PlayerPropsState,
} from '../LiveChanelPlayer/LiveChanelPlayer'
import { useOttOverlay } from '../../../contexts'
import { FetchAssetData } from '../API/API'
import {
  asset_type_enum,
  getProgramDuration,
  IFetchAssetData,
  isCatchUpAsset,
} from '../../types'
import { he } from '../../../locales/he'
import { useEffect, useState } from 'react'
import { CatchupPlayer } from './CatchupPlayer'
import { sizedOttImage } from '../../ottImage'
import {
  playerConfig,
  subtitlesData,
  utils,
} from '../../../components/Player/Helpers'
import { useGeoManifestFetchStatus } from '../../../components/Player/Helpers/useGeoManifestFetch'
import Cookies from 'universal-cookie'
import { IConfigFile } from '../../../utils'

const getAssetEntryId = ({
  entryId,
  metas,
  country,
}: {
  metas: {
    E000?: { objectType: string; value: string }
    E001?: { objectType: string; value: string }
    E010?: { objectType: string; value: string }
    E011?: { objectType: string; value: string }
    E100?: { objectType: string; value: string }
    E101?: { objectType: string; value: string }
    E110?: { objectType: string; value: string }
    E111?: { objectType: string; value: string }
  }
  entryId: ''
  country: string
}) => {
  if (entryId && entryId !== '') {
    return entryId
  }
  const cookies = new Cookies()
  const mobile = +(utils.isMobile() || playerConfig.inWebView)
  const isGlobal = +(country !== 'Israel')
  const subTitlesOn = +!!cookies.get(
    subtitlesData.video_accessibility_cookie_name
  )
  const withAccessibility = +subTitlesOn
  const entryRef = ('E' +
    mobile +
    isGlobal +
    withAccessibility) as keyof typeof metas
  return metas[entryRef]?.value
}

export const PlayerView = ({
  MEDIAID,
  configFile,
}: {
  MEDIAID: string
  configFile: IConfigFile
}) => {
  const { ks } = useOttOverlay()
  const [props, setProps] = useState<PlayerPropsState>({
    background: '',
    desktopLogo: '',
    mobileLogo: '',
    assetData: null,
    fetched: false,
    isCatchup: null,
    isTrailer: null,
    isMovie: null,
  })
  const { country, settled } = useGeoManifestFetchStatus()

  useEffect(() => {
    const fetchMediaInfo = () => {
      // Wait for the geo probe to settle before choosing the entry: a live
      // asset has an empty entryId and picks its Israel-vs-global entry from
      // `country` (getAssetEntryId). Fetching before geo settles would lock in
      // the default-'Israel' entry and never switch abroad. (VOD has a direct
      // entryId, so getAssetEntryId ignores country either way.)
      if (props.fetched || !settled) {
        return
      }
      try {
        FetchAssetData(MEDIAID, ks).then(response => {
          const data = response.data as IFetchAssetData
          const obj = data.result?.objects?.[0]
          if (obj) {
            const newData = {
              fetched: true,
              background:
                obj.images.find(o => o.imageTypeId === 148)?.url || '',
              desktopLogo: '',
              mobileLogo: '',
              assetData: {
                referenceId: obj.externalId || '',
                duration:
                  obj.objectType === 'KalturaProgramAsset'
                    ? getProgramDuration(obj)
                    : 0,
                house: '',
                entryId: getAssetEntryId({
                  entryId: obj.entryId,
                  metas: obj.metas,
                  country,
                }),
                name: obj.name || he.liveBroadcast,
                description: obj.description,
                startDate: obj.startDate,
                endDate: obj.endDate,
                type: obj.type,
                createDate: obj.createDate,
              },
            } as PlayerPropsState

            const img = obj.images?.find(
              img => img?.imageTypeName === '16x9'
            )?.url
            if (data.result.objects?.[0].type === 0) {
              newData.background = sizedOttImage(img, 1920, 1080, 70) ?? ''
            } else {
              const logoImage = obj.metas.BackgroundColor?.value
              const mobileImage = obj.images?.find(
                img => img?.imageTypeName === '2x3'
              )?.url

              if (img && logoImage) {
                newData.background = sizedOttImage(img, 1920, 1080, 70) ?? ''
                newData.desktopLogo = logoImage
                newData.mobileLogo = mobileImage
                console.log('Positive result received.')
              } else {
                console.warn('fetchMediaInfo: No image found')
              }
            }
            newData.isCatchup = isCatchUpAsset(obj)

            newData.isTrailer = obj.type === asset_type_enum.Trailer
            newData.isMovie = obj.type === asset_type_enum.Movies
            setProps(newData)
          } else {
            console.warn('fetchMediaInfo: No data or empty result')
          }
        })
      } catch (error) {
        console.error('Error fetching media information:', error)
      }
    }

    fetchMediaInfo()
  }, [MEDIAID, country, settled, ks, props.fetched])

  /*if (!(props.assetData && props.fetched)) {
    return null
  }
*/
  if (props.isCatchup || props.isTrailer || props.isMovie) {
    return (
      <CatchupPlayer
        MEDIAID={MEDIAID || ''}
        key={MEDIAID}
        playerProps={props}
      />
    )
  }
  return (
    <LiveChanelPlayer
      MEDIAID={MEDIAID || ''}
      key={MEDIAID}
      playerProps={props}
      configFile={configFile}
    />
  )
}
